import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const gameKeys = {
    byId: (id) => ['games', 'by-id', id],
    submissions: (id) => ['games', 'submissions', id],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
export function useGame(gameId) {
    return useQuery({
        queryKey: gameId ? gameKeys.byId(gameId) : ['games', 'by-id', 'none'],
        enabled: !!gameId,
        queryFn: async () => {
            if (!gameId)
                return null;
            const { data, error } = await supabase.from('games').select('*').eq('id', gameId).maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/** Suivi en temps réel des soumissions d'un game multi. */
export function useGameSubmissions(gameId, options) {
    return useQuery({
        queryKey: gameId ? gameKeys.submissions(gameId) : ['games', 'submissions', 'none'],
        enabled: !!gameId,
        refetchInterval: options?.refetchInterval ?? false,
        queryFn: async () => {
            if (!gameId)
                return [];
            const { data, error } = await supabase.rpc('get_multi_game_submissions', { p_game_id: gameId });
            if (error)
                throw error;
            return (data ?? []);
        },
    });
}
export function useStartSoloGame() {
    return useMutation({
        mutationFn: async (input) => {
            const { data, error } = await supabase.rpc('start_solo_game', {
                p_game_type: input.gameType,
                p_mode: input.mode,
            });
            if (error)
                throw error;
            const row = (Array.isArray(data) ? data[0] : data);
            if (!row)
                throw new Error('start_solo_game returned no row');
            return {
                gameId: row.game_id,
                seed: (row.seed ?? null),
                config: (row.config ?? {}),
            };
        },
    });
}
export function useStartNextGame() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const { data, error } = await supabase.rpc('start_next_game', {
                p_room_id: input.roomId,
                p_game_type: input.gameType,
                p_config: input.config,
            });
            if (error)
                throw error;
            return data;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['rooms'] });
        },
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Mutations — soumission
// ────────────────────────────────────────────────────────────────────────────
export function useSubmitSoloScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const { data, error } = await supabase.rpc('submit_solo_score', {
                p_game_id: input.gameId,
                p_score: input.score,
                p_score_meta: (input.meta ?? {}),
                p_duration_ms: input.durationMs,
            });
            if (error)
                throw error;
            return data ?? null;
        },
        onSuccess: (_d, input) => {
            void qc.invalidateQueries({ queryKey: gameKeys.byId(input.gameId) });
            void qc.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}
export function useSubmitMultiScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const { error } = await supabase.rpc('submit_multi_score', {
                p_game_id: input.gameId,
                p_score: input.score,
                p_score_meta: (input.meta ?? {}),
            });
            if (error)
                throw error;
        },
        onSuccess: (_d, input) => {
            void qc.invalidateQueries({ queryKey: gameKeys.submissions(input.gameId) });
        },
    });
}
export function useFinalizeMultiGame() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (gameId) => {
            const { data, error } = await supabase.rpc('finalize_multi_game', { p_game_id: gameId });
            if (error)
                throw error;
            return data;
        },
        onSuccess: (_d, gameId) => {
            void qc.invalidateQueries({ queryKey: gameKeys.byId(gameId) });
            void qc.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}
